/**
 * Thin WebAuthn / FIDO2 helpers for the browser side.
 *
 * These wrap the raw navigator.credentials API into base64url exchanges
 * that match what py-webauthn expects on the server.
 */

export function browserSupportsWebAuthn(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential !== 'undefined'
  );
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlToBuffer(b64: string): ArrayBuffer {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
}

/**
 * Perform a WebAuthn registration ceremony.
 *
 * Takes the server's publicKeyCredentialCreationOptions (from /auth/passkey/register/options)
 * and returns the credential response ready to POST to /auth/passkey/register/verify.
 */
export async function startRegistration(
  options: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  // Decode the server's base64url-encoded fields into ArrayBuffers.
  const publicKey = { ...options } as Record<string, unknown>;

  publicKey.challenge = base64urlToBuffer(publicKey.challenge as string);

  if (publicKey.user && typeof publicKey.user === 'object') {
    const user = { ...(publicKey.user as Record<string, unknown>) };
    user.id = base64urlToBuffer(user.id as string);
    publicKey.user = user;
  }

  if (Array.isArray(publicKey.excludeCredentials)) {
    publicKey.excludeCredentials = (publicKey.excludeCredentials as Array<Record<string, unknown>>).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id as string),
    }));
  }

  const credential = (await navigator.credentials.create({
    publicKey: publicKey as unknown as PublicKeyCredentialCreationOptions,
  })) as PublicKeyCredential;

  const response = credential.response as AuthenticatorAttestationResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(response.attestationObject),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
    },
    transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/**
 * Perform a WebAuthn authentication ceremony.
 */
export async function startAuthentication(
  options: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const publicKey = { ...options } as Record<string, unknown>;

  publicKey.challenge = base64urlToBuffer(publicKey.challenge as string);

  if (Array.isArray(publicKey.allowCredentials)) {
    publicKey.allowCredentials = (publicKey.allowCredentials as Array<Record<string, unknown>>).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id as string),
    }));
  }

  const credential = (await navigator.credentials.get({
    publicKey: publicKey as unknown as PublicKeyCredentialRequestOptions,
  })) as PublicKeyCredential;

  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToBase64url(response.authenticatorData),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}
